// User geolocation — strictly opt-in.
//
// We NEVER call navigator.geolocation without explicit user action.
// The location is stored in localStorage (one key, lat/lon/timestamp)
// so the user doesn't get re-prompted on every page load.
//
// This is the only feature that triggers any privacy disclosure beyond
// "we store your preferences locally" — see /privacy.

export type UserLocation = {
  lat: number;
  lon: number;
  /** ms since epoch when the browser reported this. */
  captured_at: number;
  /** Browser-reported accuracy radius in meters (often very wide). */
  accuracy_m: number;
};

const KEY = 'frozen-fly:user-location-v1';
const STALE_MS = 24 * 3600 * 1000; // 24h — re-prompt if the cached value is older

type LocListener = (loc: UserLocation | null) => void;
const listeners = new Set<LocListener>();

export function getStoredLocation(): UserLocation | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const loc = JSON.parse(raw) as UserLocation;
    if (Date.now() - loc.captured_at > STALE_MS) return null;
    return loc;
  } catch { return null; }
}

export function clearStoredLocation(): void {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  for (const l of listeners) l(null);
}

export function onLocationChange(fn: LocListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Prompt the browser for the user's location. Returns null if the user
 * denies, the browser doesn't support geolocation, or the request times out.
 *
 * Only called from explicit user action (the "Use my location" button).
 */
export function requestLocation(): Promise<UserLocation | null> {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc: UserLocation = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          captured_at: Date.now(),
          accuracy_m: pos.coords.accuracy,
        };
        try { localStorage.setItem(KEY, JSON.stringify(loc)); } catch { /* ignore */ }
        for (const l of listeners) l(loc);
        resolve(loc);
      },
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 30 * 60 * 1000 },
    );
  });
}

/** Haversine distance in km between two lat/lon points. */
export function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/**
 * Convenience: distance in miles. Default unit for this US-focused
 * dashboard. Round to nearest 10 above 100mi for readability.
 */
export function distanceMi(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  return distanceKm(a, b) * 0.621371;
}

/** Format a distance for display in miles: "12 mi" / "180 mi" / "1,100 mi". */
export function formatMiles(mi: number): string {
  if (mi < 10) return `${mi.toFixed(1)} mi`;
  if (mi < 100) return `${mi.toFixed(0)} mi`;
  return `${Math.round(mi).toLocaleString()} mi`;
}
